import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Modal from '../../components/common/Modal';
import { ADMIN_ALL_USER_PLANS_QUERY } from '../../graphql/queries';
import {
  ADMIN_CREATE_USER_PLAN_MUTATION,
  ADMIN_UPDATE_USER_PLAN_MUTATION,
  ADMIN_DELETE_USER_PLAN_MUTATION,
} from '../../graphql/mutations';
import styles from './AdminUserPlans.module.css';
import Icon from '../../components/common/Icon';

const GB = 1024 * 1024 * 1024;
const TB = 1024 * GB;

const trimmed = (n: number): string => String(Number(n.toFixed(2)));

function formatStorage(bytes: number): string {
  if (bytes >= TB) return `${trimmed(bytes / TB)} TB`;
  if (bytes >= GB) return `${trimmed(bytes / GB)} GB`;
  return `${trimmed(bytes / (1024 * 1024))} MB`;
}

function bytesFromInput(value: number, unit: 'GB' | 'TB'): number {
  return unit === 'TB' ? value * TB : value * GB;
}

function inputFromBytes(bytes: number): { value: number; unit: 'GB' | 'TB' } {
  if (bytes >= TB && bytes % TB === 0) return { value: bytes / TB, unit: 'TB' };
  return { value: bytes / GB, unit: 'GB' };
}

interface UserPlan {
  id: string;
  name: string;
  accountStorage: number;
  filesDownloadPerMonth: number;
  downloadGbPerMonth: number;
  schedulingPriority: number;
  gaussianPerMonth: number;
  limitedTime: boolean;
  limitedTimeDays: number;
  allowSelectForUpgrade: boolean;
  maxSubusers: number;
  userCount: number;
  createdAt: string;
}

interface PlanForm {
  name: string;
  storageValue: number;
  storageUnit: 'GB' | 'TB';
  filesDownloadPerMonth: number;
  downloadGbPerMonth: number;
  schedulingPriority: number;
  gaussianPerMonth: number;
  limitedTime: boolean;
  limitedTimeDays: number;
  allowSelectForUpgrade: boolean;
  maxSubusers: number;
}

const emptyForm = (): PlanForm => ({
  name: '',
  storageValue: 100,
  storageUnit: 'GB',
  filesDownloadPerMonth: 10,
  downloadGbPerMonth: 0,
  schedulingPriority: 1,
  gaussianPerMonth: 0,
  limitedTime: false,
  limitedTimeDays: 15,
  allowSelectForUpgrade: false,
  maxSubusers: 0,
});

const AdminUserPlans = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<UserPlan | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserPlan | null>(null);
  const [form, setForm] = useState<PlanForm>(emptyForm());
  const [formError, setFormError] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const { data, loading, error, refetch } = useQuery(ADMIN_ALL_USER_PLANS_QUERY, { fetchPolicy: 'cache-and-network' });
  const plans: UserPlan[] = data?.adminAllUserPlans ?? [];

  const [createPlan, { loading: creating }] = useMutation(ADMIN_CREATE_USER_PLAN_MUTATION, {
    onCompleted: () => { setShowModal(false); setForm(emptyForm()); refetch(); },
    onError: (e) => setFormError(e.message),
  });

  const [updatePlan, { loading: updating }] = useMutation(ADMIN_UPDATE_USER_PLAN_MUTATION, {
    onCompleted: () => { setShowModal(false); setEditTarget(null); setForm(emptyForm()); refetch(); },
    onError: (e) => setFormError(e.message),
  });

  const [deletePlan, { loading: deleting }] = useMutation(ADMIN_DELETE_USER_PLAN_MUTATION, {
    onCompleted: () => { setDeleteTarget(null); refetch(); },
    onError: (e) => setDeleteError(e.message),
  });

  const openDelete = (plan: UserPlan) => {
    setDeleteError('');
    setDeleteTarget(plan);
  };

  const openCreate = () => {
    setEditTarget(null);
    setForm(emptyForm());
    setFormError('');
    setShowModal(true);
  };

  const openEdit = (plan: UserPlan) => {
    setEditTarget(plan);
    const { value, unit } = inputFromBytes(plan.accountStorage);
    setForm({ name: plan.name, storageValue: value, storageUnit: unit, filesDownloadPerMonth: plan.filesDownloadPerMonth, downloadGbPerMonth: plan.downloadGbPerMonth ?? 0, schedulingPriority: plan.schedulingPriority ?? 1, gaussianPerMonth: plan.gaussianPerMonth ?? 0, limitedTime: plan.limitedTime ?? false, limitedTimeDays: plan.limitedTimeDays ?? 15, allowSelectForUpgrade: plan.allowSelectForUpgrade ?? false, maxSubusers: plan.maxSubusers ?? 0 });
    setFormError('');
    setShowModal(true);
  };

  const handleSubmit = () => {
    if (!form.name.trim()) { setFormError('Plan name is required'); return; }
    if (form.storageValue <= 0) { setFormError('Storage must be greater than 0'); return; }
    if (form.filesDownloadPerMonth <= 0) { setFormError('Download limit must be greater than 0'); return; }
    if (!Number.isFinite(form.gaussianPerMonth) || form.gaussianPerMonth < 0) { setFormError('Gaussian splats per month cannot be negative'); return; }
    if (!Number.isInteger(form.maxSubusers) || form.maxSubusers < 0) { setFormError('Max subusers must be a whole number, 0 or more'); return; }
    if (form.limitedTime && (!Number.isFinite(form.limitedTimeDays) || form.limitedTimeDays < 1)) { setFormError('Limited-time access must be at least 1 day'); return; }
    setFormError('');
    const accountStorage = bytesFromInput(form.storageValue, form.storageUnit);
    const shared = {
      name: form.name,
      accountStorage,
      filesDownloadPerMonth: form.filesDownloadPerMonth,
      downloadGbPerMonth: form.downloadGbPerMonth,
      schedulingPriority: form.schedulingPriority,
      gaussianPerMonth: form.gaussianPerMonth,
      limitedTime: form.limitedTime,
      limitedTimeDays: form.limitedTimeDays,
      allowSelectForUpgrade: form.allowSelectForUpgrade,
      maxSubusers: form.maxSubusers,
    };
    if (editTarget) {
      updatePlan({ variables: { id: editTarget.id, ...shared } });
    } else {
      createPlan({ variables: shared });
    }
  };

  const saving = creating || updating;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-2 text-xs text-on-surface-variant mb-2">
            <button onClick={() => navigate('/admin')} className="hover:text-primary transition-colors">Admin</button>
            <Icon name="chevron_right" className="text-sm" />
            <span>User Plans</span>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-50 text-yellow-600 rounded-lg">
                <Icon name="workspace_premium" />
              </div>
              <div>
                <h2 className="text-2xl font-bold tracking-tight text-on-surface font-headline">User Plans</h2>
                <p className="text-on-surface-variant text-sm">Define storage and download quotas for each plan tier</p>
              </div>
            </div>
            <button className="btn-primary py-2 px-5 rounded-lg text-sm" onClick={openCreate}>
              <Icon name="add" className="text-sm" />
              New Plan
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-on-surface-variant">
            <Icon name="refresh" className="animate-spin mr-2" />
            Loading plans...
          </div>
        ) : error && !data ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <Icon name="error" className="text-3xl text-error" />
            <h3 className="text-lg font-bold text-on-surface mt-2 mb-2">Could not load the plans</h3>
            <p className="text-sm text-on-surface-variant mb-4">{error.message}</p>
            <button className="btn-primary py-2 px-5 rounded-lg text-sm" onClick={() => refetch()}>
              Try again
            </button>
          </div>
        ) : (
          <div className={styles.plansGrid}>
            {plans.map((plan) => (
              <div key={plan.id} className={styles.planCard}>
                <div className={styles.planHeader}>
                  <div>
                    <h3 className={styles.planName}>{plan.name}</h3>
                    <p className={styles.planUsers}>
                      {plan.userCount} {plan.userCount === 1 ? 'user' : 'users'}
                    </p>
                  </div>
                  <div className={styles.planActions}>
                    <button
                      onClick={() => openEdit(plan)}
                      className="p-2 hover:bg-blue-50 rounded text-blue-600 transition-colors"
                      title="Edit plan"
                    >
                      <Icon name="edit" className="text-lg" />
                    </button>
                    <button
                      onClick={() => openDelete(plan)}
                      className="p-2 hover:bg-error/10 rounded text-error transition-colors"
                      title="Delete plan"
                    >
                      <Icon name="delete" className="text-lg" />
                    </button>
                  </div>
                </div>

                <div className={styles.planStats}>
                  <div className={styles.planStat}>
                    <Icon name="storage" className="text-lg text-blue-500" />
                    <div>
                      <p className={styles.statLabel}>Account Storage</p>
                      <p className={styles.statValue}>{formatStorage(plan.accountStorage)}</p>
                    </div>
                  </div>
                  <div className={styles.planStat}>
                    <Icon name="download" className="text-lg text-green-500" />
                    <div>
                      <p className={styles.statLabel}>Downloads / Month</p>
                      <p className={styles.statValue}>{plan.filesDownloadPerMonth}</p>
                    </div>
                  </div>
                  <div className={styles.planStat}>
                    <Icon name="data_usage" className="text-lg text-rose-500" />
                    <div>
                      <p className={styles.statLabel}>Download GB / Month</p>
                      <p className={styles.statValue}>{plan.downloadGbPerMonth > 0 ? `${plan.downloadGbPerMonth} GB` : 'No limit'}</p>
                    </div>
                  </div>
                  <div className={styles.planStat}>
                    <Icon name="speed" className="text-lg text-purple-500" />
                    <div>
                      <p className={styles.statLabel}>Scheduling Priority</p>
                      <p className={styles.statValue}>{plan.schedulingPriority ?? 1}</p>
                    </div>
                  </div>
                  <div className={styles.planStat}>
                    <Icon name="blur_on" className="text-lg text-indigo-500" />
                    <div>
                      <p className={styles.statLabel}>Gaussian Splats / Month</p>
                      <p className={styles.statValue}>{plan.gaussianPerMonth ?? 0}</p>
                    </div>
                  </div>
                  <div className={styles.planStat}>
                    <Icon name="group_add" className="text-lg text-teal-500" />
                    <div>
                      <p className={styles.statLabel}>Max Subusers</p>
                      <p className={styles.statValue}>{plan.maxSubusers ? plan.maxSubusers : 'Unlimited'}</p>
                    </div>
                  </div>
                  <div className={styles.planStat}>
                    <Icon name="hourglass_top" className="text-lg text-amber-500" />
                    <div>
                      <p className={styles.statLabel}>Platform Access</p>
                      <p className={styles.statValue}>{plan.limitedTime ? `${plan.limitedTimeDays} days` : 'Unlimited'}</p>
                    </div>
                  </div>
                  <div className={styles.planStat}>
                    <Icon name="upgrade" className="text-lg text-teal-500" />
                    <div>
                      <p className={styles.statLabel}>Upgrade Option</p>
                      <p className={styles.statValue}>{plan.allowSelectForUpgrade ? 'Offered' : 'Not offered'}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {plans.length === 0 && (
              <div className="col-span-full bg-white rounded-xl p-12 text-center">
                <div className="w-16 h-16 bg-yellow-50 text-yellow-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon name="workspace_premium" className="text-3xl" />
                </div>
                <h3 className="text-lg font-bold text-on-surface mb-2">No plans yet</h3>
                <p className="text-sm text-on-surface-variant mb-4">Create your first user plan to get started.</p>
                <button className="btn-primary py-2 px-5 rounded-lg text-sm" onClick={openCreate}>
                  <Icon name="add" className="text-sm" />
                  Create Plan
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create / Edit modal */}
      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditTarget(null); }}
        title={editTarget ? `Edit Plan — ${editTarget.name}` : 'Create New Plan'}
      >
        <div className="space-y-5">
          {formError && (
            <div className="p-3 bg-error-container rounded-lg flex items-center gap-2 text-sm text-on-error-container">
              <Icon name="error" className="text-sm" />
              {formError}
            </div>
          )}

          <div className="space-y-1.5">
            <label htmlFor="plan-name" className="text-xs font-bold font-label uppercase tracking-wider text-on-surface-variant">Plan Name</label>
            <input
              id="plan-name"
              className="input-field"
              placeholder="e.g. Professional"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="plan-storage" className="text-xs font-bold font-label uppercase tracking-wider text-on-surface-variant">Account Storage</label>
            <div className="flex gap-2">
              <input
                id="plan-storage"
                className="input-field"
                type="number"
                min={1}
                value={form.storageValue}
                onChange={(e) => setForm((p) => ({ ...p, storageValue: Number(e.target.value) }))}
              />
              <select
                aria-label="Storage unit"
                className="input-field w-24 shrink-0"
                value={form.storageUnit}
                onChange={(e) => setForm((p) => ({ ...p, storageUnit: e.target.value as 'GB' | 'TB' }))}
              >
                <option value="GB">GB</option>
                <option value="TB">TB</option>
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="plan-downloads" className="text-xs font-bold font-label uppercase tracking-wider text-on-surface-variant">File Downloads per Month</label>
            <input
              id="plan-downloads"
              className="input-field"
              type="number"
              min={1}
              value={form.filesDownloadPerMonth}
              onChange={(e) => setForm((p) => ({ ...p, filesDownloadPerMonth: Number(e.target.value) }))}
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="plan-download-gb" className="text-xs font-bold font-label uppercase tracking-wider text-on-surface-variant">Download GB per Month</label>
            <input
              id="plan-download-gb"
              className="input-field"
              type="number"
              min={0}
              value={form.downloadGbPerMonth}
              onChange={(e) => setForm((p) => ({ ...p, downloadGbPerMonth: Math.max(0, Number(e.target.value) || 0) }))}
            />
            <p className="text-xs text-on-surface-variant">
              Caps how many GB of original files a user can pull per month — the egress guard for
              plans that allow big scans. 0 = no GB limit (only the count above applies).
            </p>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="plan-gaussian" className="text-xs font-bold font-label uppercase tracking-wider text-on-surface-variant">Gaussian Splats per Month</label>
            <input
              id="plan-gaussian"
              className="input-field"
              type="number"
              min={0}
              value={form.gaussianPerMonth}
              onChange={(e) => setForm((p) => ({ ...p, gaussianPerMonth: Number(e.target.value) }))}
            />
            <p className="text-xs text-on-surface-variant">GPU trainings included per month for users on this plan. 0 = none.</p>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="plan-subusers" className="text-xs font-bold font-label uppercase tracking-wider text-on-surface-variant">Max Subusers</label>
            <input
              id="plan-subusers"
              className="input-field"
              type="number"
              min={0}
              step={1}
              value={form.maxSubusers}
              onChange={(e) => setForm((p) => ({ ...p, maxSubusers: Number(e.target.value) }))}
            />
            <p className="text-xs text-on-surface-variant">
              People a main user on this plan may invite to their account (Team Management). They share this plan&apos;s limits. 0 = unlimited.
            </p>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="plan-priority" className="text-xs font-bold font-label uppercase tracking-wider text-on-surface-variant">Scheduling Priority</label>
            <select
              id="plan-priority"
              className="input-field"
              value={form.schedulingPriority}
              onChange={(e) => setForm((p) => ({ ...p, schedulingPriority: Number(e.target.value) }))}
            >
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <p className="text-xs text-on-surface-variant">Higher = this plan&apos;s conversion jobs run sooner (1–10). Drives AWS Batch fair-share.</p>
          </div>

          <div className="space-y-1.5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 accent-primary"
                checked={form.limitedTime}
                onChange={(e) => setForm((p) => ({ ...p, limitedTime: e.target.checked }))}
              />
              <span className="text-sm font-medium text-on-surface">Limited time access to platform</span>
            </label>
            <p className="text-xs text-on-surface-variant">Users on this plan can use the platform only for a set number of days after registering — then uploads and Smart View / Combined View lock until they are moved to another plan.</p>
            {form.limitedTime && (
              <div className="pt-1 space-y-1.5">
                <label htmlFor="plan-limited-days" className="text-xs font-bold font-label uppercase tracking-wider text-on-surface-variant">Access duration (days)</label>
                <input
                  id="plan-limited-days"
                  className="input-field"
                  type="number"
                  min={1}
                  value={form.limitedTimeDays}
                  onChange={(e) => setForm((p) => ({ ...p, limitedTimeDays: Number(e.target.value) }))}
                />
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 accent-primary"
                checked={form.allowSelectForUpgrade}
                onChange={(e) => setForm((p) => ({ ...p, allowSelectForUpgrade: e.target.checked }))}
              />
              <span className="text-sm font-medium text-on-surface">Allowed to select for upgrade</span>
            </label>
            <p className="text-xs text-on-surface-variant">Marks this plan as offered on the upgrade page. Stored only — not used anywhere yet.</p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container-low rounded-lg transition-colors"
              onClick={() => { setShowModal(false); setEditTarget(null); }}
            >
              Cancel
            </button>
            <button
              className="btn-primary py-2 px-5 rounded-lg text-sm disabled:opacity-70"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? (
                <><Icon name="refresh" className="text-sm animate-spin" /> Saving...</>
              ) : (
                editTarget ? 'Save Changes' : 'Create Plan'
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Plan"
      >
        <div className="space-y-4">
          {deleteError && (
            <div className="p-3 bg-error-container rounded-lg flex items-center gap-2 text-sm text-on-error-container">
              <Icon name="error" className="text-sm" />
              {deleteError}
            </div>
          )}
          <p className="text-on-surface-variant text-sm">
            Are you sure you want to delete <strong className="text-on-surface">{deleteTarget?.name}</strong>?
            {deleteTarget && deleteTarget.userCount > 0 && (
              <span className="text-amber-600"> {deleteTarget.userCount} {deleteTarget.userCount === 1 ? 'user' : 'users'} on this plan will revert to the Trial plan.</span>
            )}
          </p>
          <div className="flex justify-end gap-3">
            <button
              className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container-low rounded-lg transition-colors"
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 text-sm font-medium bg-error text-on-error rounded-lg hover:opacity-90 transition-opacity disabled:opacity-70"
              onClick={() => deleteTarget && deletePlan({ variables: { id: deleteTarget.id } })}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Plan'}
            </button>
          </div>
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default AdminUserPlans;
